import * as A from 'astronomy-engine';
import { useEffect, useRef, useState } from 'preact/hooks';
import { services } from '../app';
import { goLive, hideLabels, place, placeSource, saveJSON, setSimTime, settings, showToast } from '../state';
import { type AlertState, alertState, enableAlerts } from '../live/push';
import { WELCOME_LINK } from '../data/guide';
import { APP_NAME, APP_NAME_AR } from '../data/brand';
import { FILMS } from '../data/whatsNew';
import { startPointing, stopPointing } from './pointing';
import { WhatToShowToggles } from './WhatToShow';
import { IntroFilm } from './IntroFilm';
import { MoveCode } from './MoveIn';
import { offerAfterWelcome, tourFromWelcome } from './Guide';
import { useDialog } from './chrome';
import { Icon } from './icons';

/** Why "Yes, remind me" could not turn notifications on, as Settings says it. */
const REMIND_FAILED: Partial<Record<AlertState, string>> = {
  install: 'On iPhone and iPad, first add Ahillah to your Home Screen (Share, then Add to Home Screen), then open it from there.',
  denied: 'Notifications are blocked for Ahillah in your browser or phone settings.',
  unsupported: 'This browser cannot receive notifications from web apps.',
  unavailable: 'Notifications are not available right now. You can turn them on later in Settings.',
};

export function Onboarding({ onDone }: { onDone: () => void }) {
  // The introduction film comes first while it is offered (src/data/whatsNew.ts).
  const [step, setStep] = useState<'intro' | 'welcome' | 'see' | 'crescent' | 'point'>(FILMS.intro.offered ? 'intro' : 'welcome');
  const [busy, setBusy] = useState(false);
  const heading = useRef<HTMLHeadingElement>(null);
  // A modal layer over the sky (the film first is one of its own).
  const root = useRef<HTMLDivElement>(null);
  useDialog(root, step !== 'intro');
  // Each step starts at its heading, for screen readers and keyboards.
  useEffect(() => heading.current?.focus({ preventScroll: true }), [step]);
  // The welcome shows the night sky itself, without labels: tonight's, if it is still light out.
  useEffect(() => {
    hideLabels.value = true;
    let jumped = false;
    const toNight = () => {
      const p = place.peek();
      const obs = new A.Observer(p.lat, p.lon, p.height ?? 0);
      const now = new Date();
      const sun = A.Equator(A.Body.Sun, now, obs, true, true);
      if (A.Horizon(now, obs, sun.ra, sun.dec, 'normal').altitude <= -12) {
        if (jumped) goLive();
        jumped = false;
        return;
      }
      const dark = A.SearchAltitude(A.Body.Sun, obs, -1, now, 1, -15)?.date;
      if (dark) {
        setSimTime(new Date(dark.getTime() + 3600e3), 1);
        jumped = true;
      }
    };
    toNight();
    // Found somewhere else: that place's night instead.
    const off = place.subscribe(toNight);
    return () => {
      off();
      hideLabels.value = false;
      if (jumped) goLive();
    };
  }, []);
  // A location request belongs to the welcome that made it. Going on without it, asking again or the welcome closing
  // puts it out of date, and its late answer (a fix can take 12 seconds) changes nothing: not the place chosen since,
  // nor the step.
  const asking = useRef(0);
  useEffect(() => () => void asking.current++, []);
  // However the welcome ends, "Show me around" is offered once on the sky later (src/ui/Guide.tsx), unless it is
  // taken straight away with the link below.
  const finish = () => {
    asking.current++;
    saveJSON('onboarded', { v: 1 });
    offerAfterWelcome();
    onDone();
  };
  const locate = () => {
    if (!navigator.geolocation) {
      setStep('see');
      return;
    }
    const me = ++asking.current;
    setBusy(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        if (me !== asking.current) return;
        setBusy(false);
        place.value = { lat: pos.coords.latitude, lon: pos.coords.longitude, height: pos.coords.altitude ?? 30, name: 'My location', timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone };
        placeSource.value = 'gps';
        setStep('see');
      },
      () => {
        if (me !== asking.current) return;
        setBusy(false);
        showToast('No location, so showing the sky from London. Change it any time from the top bar.', 5000);
        setStep('see');
      },
      { timeout: 12000, maximumAge: 10 * 60e3 },
    );
  };
  // "Yes, remind me": notifications for the month alerts only, which is what was asked for, not the clear evenings,
  // space station passes and sky events they otherwise bring (those can be switched on in Settings). If notifications
  // were not allowed, or cannot be had here, nothing was subscribed: those choices go back as they were, and a note says
  // why. Allowed but not yet reaching the server, the phone is subscribed and keeps only the month alerts.
  const remind = () => {
    const before = settings.peek();
    settings.value = { ...before, pushEvening: false, pushIss: false, pushEvents: false, pushSought: true };
    void enableAlerts().then(() => {
      const st = alertState.peek();
      if (st === 'on') return;
      if (typeof Notification === 'undefined' || Notification.permission !== 'granted') {
        const s = settings.peek();
        settings.value = { ...s, pushEvening: before.pushEvening, pushIss: before.pushIss, pushEvents: before.pushEvents };
      }
      showToast(REMIND_FAILED[st] ?? REMIND_FAILED.unavailable!, 7000);
    });
    setStep('point');
  };
  const withoutLocation = () => {
    asking.current++;
    setBusy(false);
    setStep('see');
  };
  // Start pointing belongs to the welcome that asked. Choosing Explore or the tour while it still waits for motion
  // access or a first reading cancels it, so a late reading neither starts pointing nor ends the welcome twice.
  const pointing = useRef(0);
  const waiting = useRef(false);
  const point = async () => {
    if (!services.peek()) return finish();
    const me = ++pointing.current;
    waiting.current = true;
    const st = await startPointing();
    if (me !== pointing.current) return;
    waiting.current = false;
    if (st !== 'running') showToast('Pointing needs motion sensors. You can drag the sky instead.', 4500);
    finish();
  };
  const explore = () => {
    pointing.current++;
    if (waiting.current) {
      waiting.current = false;
      stopPointing();
    }
    finish();
  };
  useEffect(
    () => () => {
      pointing.current++;
      if (waiting.current) stopPointing();
    },
    [],
  );
  if (step === 'intro') return <IntroFilm onDone={() => setStep('welcome')} />;
  return (
    <div class="onboard" ref={root} role="dialog" aria-modal="true" aria-labelledby="onboard-title">
      <div class="onboard-card" key={step}>
        {step === 'see' ? (
          <>
            <h1 id="onboard-title" ref={heading} tabIndex={-1}>What would you like to see?</h1>
            <p class="lead">The stars, the planets and the Moon are always there. Switch on anything else.</p>
            <WhatToShowToggles />
            <button class="btn big" onClick={() => setStep('crescent')}>Continue</button>
            <p class="fine">You can change these any time in Sky view.</p>
          </>
        ) : step === 'crescent' ? (
          <>
            <div class="onboard-icon" aria-hidden="true"><Icon name="moon" size={52} stroke={1.3} /></div>
            <h1 id="onboard-title" ref={heading} tabIndex={-1}>Would you like a reminder of the new crescent?</h1>
            <p class="lead">Ahillah can tell you when Saudi Arabia looks for the crescent of Ramaḍān, Shawwāl and Dhū al-Ḥijjah, and what it announces.</p>
            <button class="btn big" onClick={remind}><Icon name="bell" size={19} /> Yes, remind me</button>
            <button class="btn-ghost" onClick={() => setStep('point')}>Not now</button>
          </>
        ) : step === 'welcome' ? (
          <>
            <div class="brand" aria-hidden="true">
              <span>{APP_NAME}</span>
              <span class="arabic-script" lang="ar" dir="rtl">{APP_NAME_AR}</span>
            </div>
            <h1 id="onboard-title" ref={heading} tabIndex={-1}>Your night sky, live.</h1>
            <p class="lead">Stars, planets and the Moon, named as you look up, and the satellites and shooting stars passing overhead. Made for Muslim families.</p>
            <p class="promise">No myths, no astrology, no music, no ads, and nothing about you is sold.</p>
            <button class="btn big" onClick={locate} disabled={busy}><Icon name="locate" size={19} /> {busy ? 'Finding you…' : 'Use my location'}</button>
            <button class="btn-ghost" onClick={withoutLocation}>Continue with {place.value.name}</button>
            <MoveCode />
            <p class="fine">The sky is worked out on your phone. Only a rounded location leaves it, to about 1 km, for the weather.</p>
          </>
        ) : (
          <>
            <div class="phone-anim" aria-hidden="true"><Icon name="phone" size={52} stroke={1.3} /></div>
            <h1 id="onboard-title" ref={heading} tabIndex={-1}>Hold your phone up to the sky.</h1>
            <p class="lead">Ahillah follows where you point and names what you see.</p>
            <button class="btn big" onClick={point}><Icon name="phone" size={19} /> Start pointing</button>
            <button class="btn-ghost" onClick={explore}>Explore by touch instead</button>
            <button class="link-btn" onClick={() => { explore(); tourFromWelcome(); }}>{WELCOME_LINK}</button>
          </>
        )}
      </div>
    </div>
  );
}
